/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import React, { useEffect, useState } from "react";
import classnames from "classnames";
import { Typography } from "@trussle/external-components";
import { StaticImage } from "gatsby-plugin-image";
import { AccordionVariant, IAccordionItemMobile } from "../../types/IAccordionItem";
import "./styles.scss";
import { GetVariantIcon } from "../AccordionMobile";
import { useIsInView } from "../../hooks/useIsInView";
import ToggleSVG from "./toggle.svg";
import ToggleHoveredSVG from "./toggleHovered.svg";
import OrangeFirst from "./orange-first.svg";
import GreenFirst from "./green-first.svg";

export interface IAccordionDesktop {
    accordion: IAccordionItemMobile[];
    accordionSecondary?: IAccordionItemMobile[];
    firstTabVariant?: AccordionVariant;
    secondTabVariant?: AccordionVariant;
    accordionTitle?: string;
    accordionTitleSecondary?: string;
    title?: string;
    titleSecondary?: string;
}

export const GetVariantBigImage: React.FC<{variant?: string; index: number;}> = ({ variant, index }) => {
  if (variant === "orange") {
    if (index === 0) {
      return (
        <img
          src={OrangeFirst}
          alt="decorative-icon-first"
          className="accordion-desktop-item-image accordion-desktop-item-image__first"
          loading="lazy"
        />
      );
    }
    return (
      <StaticImage
        src="./orange.png"
        alt="decorative-icon"
        className="accordion-desktop-item-image"
        placeholder="none"
        height={617}
        loading="lazy"
      />
    );
  }
  if (variant === "green") {
    if (index === 0) {
      return (
        <img
          src={GreenFirst}
          alt="title-icon"
          className="accordion-desktop-item-image accordion-desktop-item-image__first"
          placeholder="none"
          loading="lazy"
        />
      );
    }
    return (
      <StaticImage
        src="./green.png"
        alt="decorative-icon"
        className="accordion-desktop-item-image"
        placeholder="none"
        height={617}
        loading="lazy"
      />
    );
  }
  return (
    <StaticImage
      src="./purple.png"
      alt="title-icon"
      className="accordion-desktop-item-image"
      placeholder="none"
      height={617}
      loading="lazy"
    />
  );
};

const DELAY_MS_SHOW_FIRST = 800;

export const AccordionDesktop: React.FC<IAccordionDesktop> = ({
  accordion,
  accordionSecondary,
  accordionTitle,
  accordionTitleSecondary,
  firstTabVariant,
  secondTabVariant,
  title,
  titleSecondary,
}) => {
  const [sectionSelected, setSectionSelected] = useState(-1);
  const [firstAccordionActive, setFirstAccordionActive] = useState(true);
  const { ref, isInView } = useIsInView();
  const onHover = (section: number) => {
    setSectionSelected(section);
  };
  const onToggleAccordion = () => {
    setFirstAccordionActive(!firstAccordionActive);
    setSectionSelected(-1);
  };
  const selectedVariant = firstAccordionActive ? firstTabVariant : secondTabVariant;
  const selectedAccordion = firstAccordionActive ? accordion : accordionSecondary;
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isInView) {
      timeout = setTimeout(() => {
        if (sectionSelected === -1) {
          setSectionSelected(0);
        }
      }, DELAY_MS_SHOW_FIRST);
    }
    return () => timeout && clearTimeout(timeout);
  }, [setSectionSelected, isInView, sectionSelected]);

  return (
    <div
      className={classnames("accordion-desktop", `accordion-desktop-${selectedVariant}`, {
        "accordion-desktop-no-selection": sectionSelected === -1,
      })}
      ref={ref}
    >
      <div className="overlay-on-top">
        <Typography variant="h1" classes="section-title" component="p" id="accordion-desktop-section-title">
          {firstAccordionActive ? title : titleSecondary}
        </Typography>
        {accordionTitleSecondary
        && (
        <button type="button" onClick={onToggleAccordion} className="section-toggle">
          <img src={ToggleSVG} alt="toggle-icon" className="section-image" />
          <img src={ToggleHoveredSVG} alt="toggle-icon" className="section-image-hovered" />
          {firstAccordionActive ? accordionTitleSecondary : accordionTitle}
        </button>
        )}
      </div>
      <>
        {selectedAccordion!.map((item, index) => (
          <div
            key={item.title}
            className={classnames("accordion-desktop-item", {
              selected: sectionSelected === index,
              "accordion-first": index === 0,
            })}
            onClick={() => onHover(index)}
            onKeyDown={() => onHover(index)}
            role="button"
            tabIndex={0}
          >
            <div className="accordion-desktop-item-content">
              <div className="accordion-desktop-item-content-secondary-title">
                <GetVariantIcon variant={selectedVariant} />
                <span className="accordion-desktop-item-content-secondary-title-content">{item.secondaryTitle}</span>
              </div>
              <Typography variant="h3" component="p" classes="accordion-desktop-item-content-title" id={`accordion-desktop-item-content-title-${item.title}`}>
                {index + 1}
                . &nbsp;
                <span className="accordion-desktop-item-content-title-content">{item.title}</span>
              </Typography>
              <div className="accordion-desktop-item-content-content">{item.content}</div>
            </div>
            <GetVariantBigImage variant={selectedVariant} index={index} />
          </div>
        ))}
      </>
    </div>
  );
};
